import memberstackDOM from '@memberstack/dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import 'hover.css'
import { useEffect } from 'react'
import './App.css'
import './lib/amplitude'

export const memberstack = memberstackDOM.init({
  publicKey: 'pk_971c0de956b797603818',
})

import { TooltipProvider } from '@/components/shadcn/tooltip.tsx'
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react'
import { useLocalStorage } from 'usehooks-ts'
import { BrainBreakProvider } from './components/BrainBreakComponent'
import { WorkOSUser } from './components/UserProfileSettingDetail'
import { CheckoutProvider } from './hooks/useCheckout'
// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { featureFlagKeys } from './utils/data/featureFlags'
import { queryClient, trpc, trpcClient } from './utils/trpc'

export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module '@trpc/client' {
  interface OperationContext {
    batch?: boolean
    idempotent?: boolean
  }
}
// Function to show/hide the reCAPTCHA badge
export const toggleCaptchaBadge = (show: boolean) => {
  const badge = document.getElementsByClassName('grecaptcha-badge')[0]
  if (badge && badge instanceof HTMLElement) {
    badge.style.visibility = show ? 'visible' : 'hidden'
  }
}
export function App() {
  const posthog = usePostHog()

  const [user] = useLocalStorage<WorkOSUser | undefined>('_wos_user', undefined)

  const maintenanceMode = useFeatureFlagEnabled(featureFlagKeys.maintenanceMode)

  useEffect(() => {
    if (maintenanceMode === true && location.pathname !== '/service-down') {
      window.location.href = '/service-down'
    }
  }, [maintenanceMode, location.pathname])

  useEffect(() => {
    window.Chargebee.init({
      site: import.meta.env.VITE_CHARGEBEE_SITE,
      publishableKey: import.meta.env.VITE_CHARGEBEE_PUBLISHABLE_KEY,
    })
  }, [])

  useEffect(() => {
    if (user && user.email && user.id) {
      // Identify sends an event, so you want may want to limit how often you call it
      posthog?.identify(user.id, {
        email: user.email,
      })
    }
  }, [posthog, user])

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <CheckoutProvider>
          <BrainBreakProvider>
            <TooltipProvider>
              <RouterProvider
                router={router}
                context={{
                  auth: {
                    isAuthenticated: user ? true : false,
                    isAuthenticatedNextRoute: '/feeds/templates',
                  },
                }}
              />
            </TooltipProvider>
          </BrainBreakProvider>
        </CheckoutProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}
